import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
// form
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { useTheme } from '@mui/material/styles';
import { Alert, Box, Button, OutlinedInput, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// graphQL
import { VERIFY_USER, SEND_EMAIL_VERIFICATION_CODE } from 'src/graphQL/queries';
// components
import FormProvider from 'src/components/hook-form/FormProvider';
import AuthPortalLayout from 'src/layouts/auth/AuthPortalLayout';
import { AUTH_PORTALS, authButtonSx, authSecondaryButtonSx } from 'src/layouts/auth/portals';
import { secondaryButtonSx } from 'src/theme/universe/buttonSx';
import { MOTION, RADIUS, UNIVERSE_FONT, accentOf, fieldLineOf, tabularSx } from 'src/theme/universe/brand';
import { toneInkOf } from 'src/theme/universe/tones';
// hooks
import useAuth from 'src/hooks/useAuth';
import { PATH_LINGUIST } from 'src/routes/paths';

// ----------------------------------------------------------------------
// THE LEGACY 6-DIGIT CHECK. Nothing sets `profileStatus: 'unverified'` any
// more — Supabase confirms addresses for new registrations — but ~520
// pre-2024 linguist records still carry it and the resolvers behind this
// page are live, so it stays, in the auth shell every other auth state
// wears: six boxes that paste as one, the numeric keyboard on a phone, the
// one verb, the way to a new code, and a way out.
// ----------------------------------------------------------------------

const CODES = ['code1', 'code2', 'code3', 'code4', 'code5', 'code6'];

const VerifyEmail = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [verifyUser, { loading }] = useLazyQuery(VERIFY_USER, { fetchPolicy: 'no-cache' });
  const [sentVerificationCode, { loading: sending }] = useLazyQuery(SEND_EMAIL_VERIFICATION_CODE, {
    fetchPolicy: 'no-cache'
  });
  const [problem, setProblem] = useState('');
  const [resent, setResent] = useState(false);
  const inputs = useRef([]);

  const VerifyCodeSchema = Yup.object().shape(
    CODES.reduce((shape, name) => ({ ...shape, [name]: Yup.string().required() }), {})
  );
  const methods = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(VerifyCodeSchema),
    defaultValues: CODES.reduce((values, name) => ({ ...values, [name]: '' }), {})
  });
  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors }
  } = methods;

  // A pasted code fills every box at once.
  useEffect(() => {
    const first = inputs.current[0];
    if (!first) return undefined;
    const onPaste = (event) => {
      const text = (event.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
      if (!text) return;
      event.preventDefault();
      text.split('').forEach((digit, index) => setValue(CODES[index], digit));
      inputs.current[Math.min(text.length, 5)]?.focus();
    };
    first.addEventListener('paste', onPaste);
    return () => first.removeEventListener('paste', onPaste);
  }, [setValue]);

  const onSubmit = async (data) => {
    setProblem('');
    try {
      const res = await verifyUser({
        variables: { email: user.email, authCode: CODES.map((name) => data[name]).join('') }
      });
      if (!res?.data?.verifyUserEmail) throw new Error('Invalid code');
      login(res.data.verifyUserEmail);
      enqueueSnackbar('Email verified.', { variant: 'success' });
      navigate(PATH_LINGUIST.user.translationProjects, { replace: true });
    } catch (error) {
      setProblem('That code is not right, or it has expired. Check the latest email, or send a new code.');
    }
  };

  const handleSendCode = async () => {
    setProblem('');
    setResent(false);
    try {
      await sentVerificationCode({ variables: { email: user.email } });
      setResent(true);
    } catch (error) {
      setProblem('We could not send a new code. Please try again in a moment.');
    }
  };

  const missing = CODES.some((name) => errors[name]);

  return (
    <AuthPortalLayout
      portal={AUTH_PORTALS.vendor}
      title="Verify your email address"
      subtitle={`Enter the 6-digit code we emailed to ${user?.email || 'your address'}.`}
    >
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          {problem && (
            <Alert severity="error" role="alert" sx={{ borderRadius: `${RADIUS.small}px` }}>
              {problem}
            </Alert>
          )}
          {resent && !problem && (
            <Alert severity="success" sx={{ borderRadius: `${RADIUS.small}px` }}>
              A new code is on its way. It can take a minute to arrive.
            </Alert>
          )}

          <Box role="group" aria-label="Verification code">
            <Stack direction="row" spacing={1} justifyContent="center">
              {CODES.map((name, index) => (
                <Controller
                  key={name}
                  name={name}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <OutlinedInput
                      {...field}
                      inputRef={(el) => {
                        inputs.current[index] = el;
                        field.ref(el);
                      }}
                      error={Boolean(error)}
                      autoFocus={index === 0}
                      onChange={(event) => {
                        const digit = event.target.value.replace(/\D/g, '').slice(-1);
                        field.onChange(digit);
                        if (digit && index < 5) inputs.current[index + 1]?.focus();
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Backspace' && !field.value && index > 0) {
                          inputs.current[index - 1]?.focus();
                        }
                      }}
                      inputProps={{
                        maxLength: 1,
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        autoComplete: index === 0 ? 'one-time-code' : 'off',
                        'aria-label': `Digit ${index + 1} of 6`,
                        style: { textAlign: 'center', padding: 0 }
                      }}
                      sx={{
                        width: { xs: 44, sm: 52 },
                        height: { xs: 48, sm: 56 },
                        borderRadius: `${RADIUS.control}px`,
                        fontFamily: UNIVERSE_FONT,
                        fontSize: 20,
                        fontWeight: 600,
                        ...tabularSx,
                        bgcolor: 'background.paper',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: fieldLineOf(theme) },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: accentOf(theme) },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: accentOf(theme),
                          borderWidth: 1,
                          boxShadow: `0 0 0 3px ${accentOf(theme)}33`
                        },
                        '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: toneInkOf(theme, 'error') },
                        transition: `box-shadow ${MOTION.fast}ms`
                      }}
                    />
                  )}
                />
              ))}
            </Stack>
            {missing && (
              <Typography
                role="alert"
                sx={{ mt: 1, textAlign: 'center', fontFamily: UNIVERSE_FONT, fontSize: 12, fontWeight: 500, color: toneInkOf(theme, 'error') }}
              >
                Enter all six digits
              </Typography>
            )}
          </Box>

          <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={loading} sx={authButtonSx}>
            Verify email
          </LoadingButton>

          <LoadingButton fullWidth type="button" variant="outlined" loading={sending} onClick={handleSendCode} sx={authSecondaryButtonSx}>
            Send a new code
          </LoadingButton>

          <Button type="button" onClick={logout} sx={{ ...secondaryButtonSx(theme, 'small'), alignSelf: 'center', border: 0 }}>
            Sign out
          </Button>
        </Stack>
      </FormProvider>
    </AuthPortalLayout>
  );
};

export default VerifyEmail;
